<template>
  <div class="drag-overlay">
    <div
      v-for="zoneEdge in edges"
      :key="zoneEdge"
      class="zone"
      :class="[`zone-${zoneEdge}`, {visible: !!update, active: update?.edge === zoneEdge}]"
    />
    <div
      class="preview"
      :class="{visible: !!update}"
      :style="previewStyle"
    />
  </div>
</template>

<script lang="ts" setup>
import {computed, onMounted, onUnmounted, ref} from "vue";
import {NotificationEdge} from "@/settings/interfaces";
import {WindowRectangle} from "@/notification/services/notificationWindowPlacement";

interface DragOverlayUpdate {
    edge: NotificationEdge;
    /** The snap target bounds, relative to the overlay window. */
    rect: WindowRectangle;
}

const edges: NotificationEdge[] = ['left', 'right'];
const update = ref<DragOverlayUpdate | null>(null);

const previewStyle = computed(() => {
    if (!update.value) {
        return {};
    }

    return {
        left: update.value.rect.x + 'px',
        top: update.value.rect.y + 'px',
        width: update.value.rect.width + 'px',
        height: update.value.rect.height + 'px',
    };
});

onMounted(() => {
    window.ipcRenderer.on('drag-overlay-update', (_: Event, payload: DragOverlayUpdate | null) => {
        update.value = payload;
    });
});

onUnmounted(() => {
    window.ipcRenderer.removeAllListeners('drag-overlay-update');
});
</script>

<style scoped>
.drag-overlay {
    position: fixed;
    inset: 0;
    background: transparent;
    overflow: hidden;
    pointer-events: none;
}

/* The overlay draws over the user's desktop, whose colors are arbitrary:
   every element pairs a light component with a dark one (duotone), so at
   least one of the two contrasts on any wallpaper. Colors decline the
   charter tokens by opacity: primary #3513e2, lavender #e2c4ff, ink #353535. */
.zone {
    position: fixed;
    top: 0;
    bottom: 0;
    width: 8px;
    background: rgba(226, 196, 255, 0.45);
    box-shadow: 0 0 0 1px rgba(53, 53, 53, 0.30);
    opacity: 0;
    transition: opacity 0.15s ease-out, background 0.15s ease-out, box-shadow 0.15s ease-out;
}

.zone.visible {
    opacity: 1;
}

.zone.active {
    background: rgba(53, 19, 226, 0.80);
    box-shadow: inset 0 0 0 1px rgba(255, 255, 255, 0.65), 0 0 12px rgba(53, 19, 226, 0.75);
}

.zone-left {
    left: 0;
    border-radius: 0 8px 8px 0;
}

.zone-right {
    right: 0;
    border-radius: 8px 0 0 8px;
}

.preview {
    position: fixed;
    border: 2px dashed rgba(53, 19, 226, 0.85);
    /* Light veil + white outer ring: carry the shape on dark wallpapers,
       where the violet dashes alone would vanish. */
    background: rgba(255, 255, 255, 0.18);
    box-shadow: 0 0 0 1px rgba(255, 255, 255, 0.85), 0 4px 16px rgba(0, 0, 0, 0.35);
    border-radius: 12px;
    box-sizing: border-box;
    opacity: 0;
    transition: left 0.12s ease-out, top 0.12s ease-out,
    width 0.12s ease-out, height 0.12s ease-out, opacity 0.15s ease-out;
}

.preview.visible {
    opacity: 1;
}
</style>
